<template>
  <div v-loading="loading" class="talent-matrix-table">
    <div v-if="matrix.setting.productivity_size && matrix.setting.potential_size">
      <div class="filters-before-table">
        <div style="display: flex; justify-content: space-between">
          <div style="display: flex; gap: 10px">
            <search-input
              placeholder="Поиск по ФИО"
              style="width: 300px;"
              v-model="filters.search"
              @change="loadAxisUserPositions"
            >
            </search-input>
            <talent-matrix-axis-cells-select
              v-model="filters.cell_ids"
              :matrix="matrix"
              size="size"
              @selected="loadAxisUserPositions"
            ></talent-matrix-axis-cells-select>
            <user-group-select
              v-if="$canAndRulesPass(user, $permissions.GROUP.LIST)"
              v-model="filters.group_ids"
              size="size"
              @selected="loadAxisUserPositions"
            ></user-group-select>
            <user-unique-prop-values-select
              placeholder="Должность"
              v-model="filters.positions"
              prop="position"
              multiple
              @selected="loadAxisUserPositions"
            ></user-unique-prop-values-select>
            <user-unique-prop-values-select
              placeholder="Компания"
              v-model="filters.companies"
              prop="company"
              multiple
              @selected="loadAxisUserPositions"
            ></user-unique-prop-values-select>
            <user-unique-prop-values-select
              placeholder="Подразделение"
              v-model="filters.subdivisions"
              prop="subdivision"
              multiple
              @selected="loadAxisUserPositions"
            ></user-unique-prop-values-select>
          </div>

          <el-button
            class="button-icon-green"
            type="text"
            icon="fas fa-file-excel"
            :loading="loadingXls"
            @click="exportToExcel"
          >
            Экспорт в XLSX
          </el-button>
        </div>
      </div>

      <div>
        <el-table :data="axis_user_positions" v-loading="loading">
          <el-table-column width="150" prop="user.persnum" label="Перс. №"></el-table-column>
          <el-table-column prop="user.full_name" label="ФИО"></el-table-column>
          <el-table-column width="500" label="Категория матрицы">
            <template v-slot="scope">
              <div :style="{'background-color': (scope.row.cell ? scope.row.cell.color : '#ffffff')}" class="matrix_status">
                <div style="background-color: rgba(255, 255, 255, 0.7)">
                  <collapse-text-with-tooltip
                    style="cursor: pointer"
                    v-if="scope.row.cell"
                    :text="scope.row.cell.name"
                    :maxLength="70"
                    trigger="click"
                  ></collapse-text-with-tooltip>
                  <span v-else>Без категории</span>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column width="200" label="Потенциал">
            <template v-slot="scope">
              {{ scope.row.potential_value }}
              <el-tooltip v-if="scope.row.potential_card_id" content="Перейти в карту" placement="top">
                <router-link
                  class="table-router-link"
                  :to="{ name: 'Card', params: { id: scope.row.potential_card_id } }"
                  target="_blank"
                >
                  <el-button
                    class="table-action-button"
                    round
                    size="mini"
                    icon="fas fa-external-link-alt"
                  >
                  </el-button>
                </router-link>
              </el-tooltip>
              <el-tooltip v-if="scope.row.potential_evaluation_id" content="Смотреть отчет по оценке" placement="top">
                <el-button
                  class="table-action-button"
                  round
                  size="mini"
                  icon="fas fa-chart-bar"
                  @click="showEvaluateResult(scope.row.user, scope.row.potential_evaluation_id)"
                >
                </el-button>
              </el-tooltip>
            </template>
          </el-table-column>
          <el-table-column width="200" label="Производительность">
            <template v-slot="scope">
              {{ scope.row.productivity_value }}
              <el-tooltip v-if="scope.row.productivity_card_id" content="Перейти в карту" placement="top">
                <router-link
                  class="table-router-link"
                  :to="{ name: 'Card', params: { id: scope.row.productivity_card_id } }"
                  target="_blank"
                >
                  <el-button
                    class="table-action-button"
                    round
                    size="mini"
                    icon="fas fa-external-link-alt"
                  >
                  </el-button>
                </router-link>
              </el-tooltip>
              <el-tooltip v-if="scope.row.productivity_evaluation_id" content="Смотреть отчет по оценке" placement="top">
                <el-button
                  class="table-action-button"
                  round
                  size="mini"
                  icon="fas fa-chart-bar"
                  @click="showEvaluateResult(scope.row.user, scope.row.productivity_evaluation_id)"
                >
                </el-button>
              </el-tooltip>
            </template>
          </el-table-column>
        </el-table>
        <paginator
          :paginator.sync="paginator"
          use-router-query-param
          @page-changed="loadAxisUserPositions"
        ></paginator>
      </div>
    </div>
    <div v-else>
      <p style="text-align: center; color: #909399; font-weight: bold">
        Невозможно построить матрицу, пока не выбран ее размер.<br>
        Потенциал (ось Y) и Производительность (ось X).
      </p>
    </div>

    <evaluate-result-dialog
      ref="evaluateResultDialog"
    >
    </evaluate-result-dialog>
  </div>
</template>

<script>
import UserGroupSelect from "@/components/filters/users/UserGroupSelect.vue";
import UserUniquePropValuesSelect from "@/components/filters/users/UserUniquePropValuesSelect.vue";
import TalentMatrixAxisCellsSelect from "@/modules/talentMatrix/components/TalentMatrixAxisCellsSelect.vue";
import Paginator from "@/components/Paginator.vue";
import {paginator} from "@/mixins/paginator";
import SearchInput from "@/components/filters/SearchInput.vue";
import {downloader} from "@/mixins/downloader";
import CollapseTextWithTooltip from "@/components/CollapseTextWithTooltip.vue";
import EvaluateResultDialog from "@/modules/evaluation/components/evaluation/EvaluateResultDialog.vue";
import {mapGetters} from "vuex";

export default {
  name: 'talent-matrix-table-view',
  components: {
    EvaluateResultDialog,
    CollapseTextWithTooltip,
    SearchInput,
    Paginator,
    UserGroupSelect,
    UserUniquePropValuesSelect,
    TalentMatrixAxisCellsSelect,
  },
  props: {
    matrix: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    ...mapGetters(['user']),
  },
  mixins: [paginator, downloader],
  data() {
    return {
      loading: false,
      loadingXls: false,
      axis_user_positions: [],
      filters: {
        search: '',
        cell_ids: [],
        positions: [],
        group_ids: [],
        companies: [],
        subdivisions: [],
      },
    }
  },
  mounted() {
    this.loadAxisUserPositions();
  },
  methods: {
    showEvaluateResult(user, evaluationId) {
      this.$refs.evaluateResultDialog.open(user, evaluationId);
    },
    exportToExcel() {
      this.loadingXls = true;
      this.downloadFile(
        this.matrix.name + '.xlsx',
        this.$api.exportImport.export,
        {
          entity: 'talent_matrix',
          data: {
            ...this.filters,
            matrix_id: this.matrix.id,
          }}
      )
        .finally(()=>{
          this.loadingXls = false;
        })
    },
    loadAxisUserPositions() {
      this.loading = true;
      this.$api.talentMatrix.matrixCellUserPositionsSearch({
        ...this.filters,
        matrix_id: this.matrix.id,
        table_view: true,
        expand: ['user', 'cell'].join(),
        [this.paginator.param]: this.paginator.page,
        [this.paginator.paramPerPage]: this.paginator.perPage,
      })
        .then(({data, paginator}) => {
          this.axis_user_positions = data.axis_user_positions;
          this.paginatorFill(paginator);
        })
        .finally(() => {
          this.loading = false;
        })
    },
  },
}
</script>

<style scoped>
  .talent-matrix-table {
    .button-icon-green {
      margin-left: 10px;
    }

    .matrix_status {
      text-align: center;
      border-radius: 7px;
      width: fit-content;
    }

    .matrix_status>div {
      padding: 0 5px;
      border-radius: 7px;
    }
  }
</style>
